import { Step, StepId } from "@redotech/redo-return-ui/flows/step";

/**
 * Orders step IDs to match how stepsToModel + stepsBuild will assign numeric IDs.
 * This ensures that when we save positions indexed by model step number,
 * they map correctly when the flow is reloaded and step IDs are reassigned.
 *
 * The order matches stepsBuild's behavior:
 * 1. Connected steps get IDs via recursive traversal (matching getId recursion)
 * 2. Disconnected steps get IDs in Map iteration order (matching Set iteration)
 */
export function getStepIdsInModelOrder(steps: Map<StepId, Step>): string[] {
  const orderedStepIds: string[] = [];
  const assigned = new Set<string>();

  // Recursively assign order following the connection graph
  const assignInTraversalOrder = (stepId: string | undefined) => {
    if (!stepId || assigned.has(stepId)) return;
    assigned.add(stepId);
    orderedStepIds.push(stepId);

    const step = steps.get(stepId);
    if (step) {
      // Process all downstream connections (handles branches)
      const downstreams = step.downstream();
      for (const downstream of downstreams) {
        if (downstream.id) {
          assignInTraversalOrder(downstream.id);
        }
      }
    }
  };

  // Start from the first step (matches stepsToModel's stepFns.set(step.start, undefined!))
  const startStep = steps.get("start");
  const firstStepId = startStep?.downstream()[0]?.id;
  assignInTraversalOrder(firstStepId);

  // Add remaining disconnected nodes in Map iteration order
  for (const [stepId] of steps.entries()) {
    if (stepId !== "start" && !assigned.has(stepId)) {
      orderedStepIds.push(stepId);
    }
  }

  return orderedStepIds;
}

export function reconstructNodePositionsMap(
  positionsArray: { x: number; y: number }[] | undefined,
  steps: Map<StepId, Step>,
): Map<string, { x: number; y: number }> {
  if (!positionsArray || positionsArray.length === 0) {
    return new Map();
  }

  const orderedStepIds = getStepIdsInModelOrder(steps);

  const positionsMap = new Map<string, { x: number; y: number }>();
  for (let i = 0; i < orderedStepIds.length && i < positionsArray.length; i++) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    positionsMap.set(orderedStepIds[i], positionsArray[i]);
  }

  return positionsMap;
}
